import { Fragment, type ReactNode } from "react";

/**
 * Render inline `**bold**` markdown the bill-analyzer model emits into
 * a list of React nodes. Pulls in zero deps: splits on `**` and
 * alternates body / `<strong>` runs.
 *
 * The analyzer's `priority_actions` and `executive_summary` fields
 * land verbatim from the LLM with inline bold spans
 * (`**URGENT (within 3 days):**`, `**$0.00023/kWh**`, …). Plain-text
 * rendering shows literal asterisks; this helper keeps the bold
 * emphasis without bringing in `react-markdown`.
 *
 * Returns the raw string unchanged when no markers are present so
 * the common case stays cheap (no Fragment churn, no key warnings).
 */
export function renderInlineMarkdown(input: string): ReactNode {
  if (!input.includes("**")) return input;
  const parts = input.split("**");
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i}>{part}</strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

/**
 * Render the block-level markdown a chat model emits — `#`–`####` headings,
 * `-`/`*` bullet lists, `1.` numbered lists, and paragraphs — into React
 * nodes, with `renderInlineMarkdown` handling `**bold**` inside every run.
 * Still zero deps: line-based, no AST. Anything that isn't a recognized
 * block marker stays a paragraph with its line breaks preserved, so plain
 * prose renders exactly as before.
 *
 * The caller wraps the result in a `flex flex-col gap-*` container — each
 * block is a sibling node, not nested prose.
 */
export function renderMarkdownBlocks(input: string): ReactNode {
  const lines = input.split("\n");
  const out: ReactNode[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let key = 0;

  const flushPara = () => {
    if (para.length === 0) return;
    out.push(
      <p key={key++} className="whitespace-pre-wrap">
        {renderInlineMarkdown(para.join("\n"))}
      </p>,
    );
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const items = list.items.map((item, i) => (
      <li key={i}>{renderInlineMarkdown(item)}</li>
    ));
    out.push(
      list.ordered ? (
        <ol key={key++} className="list-decimal space-y-1 pl-5">
          {items}
        </ol>
      ) : (
        <ul key={key++} className="list-disc space-y-1 pl-5">
          {items}
        </ul>
      ),
    );
    list = null;
  };
  const pushListItem = (ordered: boolean, item: string) => {
    flushPara();
    if (list && list.ordered !== ordered) flushList();
    if (!list) list = { ordered, items: [] };
    list.items.push(item);
  };

  for (const line of lines) {
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (heading) {
      flushPara();
      flushList();
      // Two visual tiers are enough inside a chat bubble: #/## read as the
      // answer's title, ###/#### as section labels within it.
      const cls =
        heading[1].length <= 2
          ? "text-base font-semibold text-foreground"
          : "text-sm font-semibold text-foreground";
      out.push(
        <p key={key++} className={cls}>
          {renderInlineMarkdown(heading[2])}
        </p>,
      );
    } else if (bullet) {
      pushListItem(false, bullet[1]);
    } else if (numbered) {
      pushListItem(true, numbered[1]);
    } else if (line.trim() === "") {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return out;
}
